import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Autosuggest from 'react-autosuggest';
import RouteTable from './RouteTable';
import { Form, Button, Row, Col } from 'react-bootstrap'; // Added Row and Col
import { FaSearch, FaSpinner, FaEraser, FaFileExcel, FaPlus } from 'react-icons/fa'; // Added FaSpinner, FaEraser, FaFileExcel, and FaPlus
import { Dropdown } from 'react-bootstrap'; // Import Dropdown component
import './RoutingGuide.scss';

const ListRoutingGuides = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [origin, setOrigin] = useState(new URLSearchParams(location.search).get('origin') || '');
  const [destination, setDestination] = useState(new URLSearchParams(location.search).get('destination') || '');
  const [providerType, setProviderType] = useState(new URLSearchParams(location.search).get('providerType') || '');
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [provider, setProvider] = useState('');
  const [providerSuggestions, setProviderSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Added isLoading state
  const [totalRecords, setTotalRecords] = useState('0'); // Added totalRecords state
  const [originCountry, setOriginCountry] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [originCountrySuggestions, setOriginCountrySuggestions] = useState([]);
  const [destinationCountrySuggestions, setDestinationCountrySuggestions] = useState([]);
  const [withSchedules, setWithSchedules] = useState(null); // New state for With Schedules
  const [providerTypeDetails, setProviderTypeDetails] = useState({}); // New state for provider type details

  // Step 1: Modify fetchRoutes to accept search parameters
  const fetchRoutes = useCallback(async (searchOrigin, searchDestination, searchProvider, searchOriginCountry, searchDestinationCountry, searchWithSchedules) => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/routes', {
        params: {
          providerType,
          origin: searchOrigin || undefined,
          destination: searchDestination || undefined,
          origin_country: searchOriginCountry || undefined,
          destination_country: searchDestinationCountry || undefined,
          with_schedules: searchWithSchedules || undefined, // Pass with_schedules parameter
          page: currentPage,
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
          providerName: searchProvider || undefined
        },
      });
      setRoutes(response.data.routes);
      setTotalPages(response.data.totalPages);
      setTotalRecords(response.data.total.toLocaleString());
    } catch (error) {
      console.error('Error fetching routes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [providerType, currentPage, sortConfig]);

  // Fetch provider type information from /api/provider_type. Pass query string parameter provider_type_name from providerType
  useEffect(() => {
    if (providerType) {
      axios.get('/api/provider_type', { params: { provider_type_name: providerType } })
        .then(response => {
          if(response && response.data) {
            console.log({ providerType, response: response.data });
            setProviderTypeDetails(response.data);
          }
        })
        .catch(error => {
          console.error('Error fetching provider type:', error);
          navigate('/routing-guide'); // Redirect on error
        });
    }
  }, [providerType, navigate]);

  const fetchProviders = useCallback(async () => {
    try {
      const response = await axios.get('/api/providers', {
        params: { providerType },
      });
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  }, [providerType]);

  const fetchProviderSuggestions = async (value, setSuggestions) => {
    try {
      const response = await axios.get('/api/provider-by-scac-and-name', { 
        params: { 
          query: value,
          providerType 
        } 
      });
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching provider suggestions:', error);
    }
  };

  // Step 2: Update useEffect to remove fetchRoutes from dependencies
  useEffect(() => {
    if (!providerType) {
      navigate('/routing-guide'); // Redirect if providerType is not present
    } else {
      // Initial fetch without search filters
      fetchRoutes('', '', '', '', '', null);
      fetchProviders();
    }
  }, [providerType, currentPage, sortConfig, fetchProviders, navigate]);

  // const handleDeleteRoute = async (routeId) => {
  //   try {
  //     await axios.delete(`/api/routes/${routeId}`);
  //     fetchRoutes(origin, destination, provider); // Trigger search with current search field values
  //     setSuccessMessage('Route successfully deleted.');
  //   } catch (error) {
  //     console.error('Error deleting route:', error);
  //     setError('Error deleting route.');
  //   }
  // };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const fetchPortSuggestions = async (value, setSuggestions) => {
    try {
      const response = await axios.get('/api/port-names-by-uncode-or-name', { params: { query: value } });
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching port suggestions:', error);
    }
  };

  // New function to fetch country suggestions
  const fetchCountrySuggestions = async (value, setSuggestions) => {
    try {
      const response = await axios.get('/api/country-autosuggest', { 
        params: { 
          query: value
        } 
      });
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching country suggestions:', error);
    }
  };

  // Step 3: Update handleSearch to pass new country values and with_schedules
  const handleSearch = () => {
    fetchRoutes(origin, destination, provider, originCountry, destinationCountry, withSchedules);
  };

  const handleClearFilters = () => {
    setOrigin('');
    setDestination('');
    setOriginCountry('');
    setDestinationCountry('');
    setProvider(''); // Clear provider field
    setWithSchedules(null); // Reset With Schedules
    fetchRoutes('', '', '', '', '', null);
  };

  const handleDownloadExcel = () => {
    const url = `/api/download-routing-guide-excel?providerType=${encodeURIComponent(providerType)}`;
    window.location.href = url;
  };

  const handleDownloadSearchResultsToExcel = () => {
    const url = `/api/download-routing-guide-search-excel?providerType=${encodeURIComponent(providerType)}&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&provider=${encodeURIComponent(provider)}&originCountry=${encodeURIComponent(originCountry)}&destinationCountry=${encodeURIComponent(destinationCountry)}&withSchedules=${withSchedules}&sortKey=${encodeURIComponent(sortConfig.key)}&sortDirection=${encodeURIComponent(sortConfig.direction)}`;
    window.location.href = url;
  };

  // Step 4: Ensure inputProps do not trigger fetchRoutes on change
  const inputProps = (name, value, onChange) => ({
    placeholder: `Enter ${name} name or code`,
    value: value || '', // Ensure value is always a string
    onChange // Only updates state, no search triggers
  });

  // Step 4: Add inputProps for country fields
  const countryInputProps = (name, value, onChange) => ({
    placeholder: `Enter ${name} description`,
    value: value || '',
    onChange
  });

  return (
    <div className="list-routing-guides" style={{ position: 'relative' }}>
      {(providerTypeDetails && providerTypeDetails.length && providerTypeDetails[0].provider_type_long_name) && 
      (
        <div className="routing-guide-listing-header">
          <h1>Routing Guides</h1>
          <h2>{providerTypeDetails[0].provider_type_long_name}</h2>
        </div>
      )}
      {successMessage && <div className="alert alert-success" role="alert">{successMessage}</div>}
      <div className="filter-section">
        <Row>
          {/* Origin Country Field */}
          <Col md={6}>
            <Form.Group controlId="originCountry" className="mb-3">
              <Form.Label>Origin Country</Form.Label>
              <Autosuggest
                suggestions={originCountrySuggestions}
                onSuggestionsFetchRequested={({ value }) => fetchCountrySuggestions(value, setOriginCountrySuggestions)}
                onSuggestionsClearRequested={() => setOriginCountrySuggestions([])}
                getSuggestionValue={suggestion => suggestion.country_cd}
                renderSuggestion={suggestion => <div>{suggestion.description}</div>}
                inputProps={{
                  placeholder: 'Enter Origin Country Description',
                  value: originCountry,
                  onChange: (e, { newValue }) => setOriginCountry(newValue)
                }}
                theme={{
                  container: 'react-autosuggest__container',
                  input: 'react-autosuggest__input',
                  suggestionsContainer: 'react-autosuggest__suggestions-container',
                  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                  suggestionsList: 'react-autosuggest__suggestions-list',
                  suggestion: 'react-autosuggest__suggestion',
                  suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                }}
              />
            </Form.Group>
          </Col>

          {/* Destination Country Field */}
          <Col md={6}>
            <Form.Group controlId="destinationCountry" className="mb-3">
              <Form.Label>Destination Country</Form.Label>
              <Autosuggest
                suggestions={destinationCountrySuggestions}
                onSuggestionsFetchRequested={({ value }) => fetchCountrySuggestions(value, setDestinationCountrySuggestions)}
                onSuggestionsClearRequested={() => setDestinationCountrySuggestions([])}
                getSuggestionValue={suggestion => suggestion.country_cd}
                renderSuggestion={suggestion => <div>{suggestion.description}</div>}
                inputProps={{
                  placeholder: 'Enter Destination Country Description',
                  value: destinationCountry,
                  onChange: (e, { newValue }) => setDestinationCountry(newValue)
                }}
                theme={{
                  container: 'react-autosuggest__container',
                  input: 'react-autosuggest__input',
                  suggestionsContainer: 'react-autosuggest__suggestions-container',
                  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                  suggestionsList: 'react-autosuggest__suggestions-list',
                  suggestion: 'react-autosuggest__suggestion',
                  suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Existing Origin and Destination Fields */}
        <Row>
          <Col md={6}>
            <Form.Group controlId="origin" className="mb-3">
              <Form.Label>Origin City, Port or Airport</Form.Label>
              <Autosuggest
                suggestions={originSuggestions}
                onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setOriginSuggestions)}
                onSuggestionsClearRequested={() => setOriginSuggestions([])}
                getSuggestionValue={suggestion => suggestion.port_code}
                renderSuggestion={suggestion => <div>{suggestion.port_name}</div>}
                inputProps={inputProps('origin', origin, (e, { newValue }) => setOrigin(newValue))} // Removed fetchRoutes from onChange
                theme={{
                  container: 'react-autosuggest__container',
                  input: 'react-autosuggest__input',
                  suggestionsContainer: 'react-autosuggest__suggestions-container',
                  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                  suggestionsList: 'react-autosuggest__suggestions-list',
                  suggestion: 'react-autosuggest__suggestion',
                  suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                }}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="destination" className="mb-3">
              <Form.Label>Destination City, Port or Airport</Form.Label>
              <Autosuggest
                suggestions={destinationSuggestions}
                onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setDestinationSuggestions)}
                onSuggestionsClearRequested={() => setDestinationSuggestions([])}
                getSuggestionValue={suggestion => suggestion.port_code}
                renderSuggestion={suggestion => <div>{suggestion.port_name}</div>}
                inputProps={inputProps('destination', destination, (e, { newValue }) => setDestination(newValue))} // Removed fetchRoutes from onChange
                theme={{
                  container: 'react-autosuggest__container',
                  input: 'react-autosuggest__input',
                  suggestionsContainer: 'react-autosuggest__suggestions-container',
                  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                  suggestionsList: 'react-autosuggest__suggestions-list',
                  suggestion: 'react-autosuggest__suggestion',
                  suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Provider and With Schedules Fields */}
        <Row>
          <Col md={6}>
            <Form.Group controlId="provider" className="mb-3">
              <Form.Label>Provider</Form.Label>
              <Autosuggest
                suggestions={providerSuggestions}
                onSuggestionsFetchRequested={({ value }) => fetchProviderSuggestions(value, setProviderSuggestions)}
                onSuggestionsClearRequested={() => setProviderSuggestions([])}
                getSuggestionValue={suggestion => suggestion.provider_name}
                renderSuggestion={suggestion => <div>{`${suggestion.provider_name} (${suggestion.provider_scac})`}</div>}
                inputProps={{
                  placeholder: 'Enter Provider Name or SCAC',
                  value: provider,
                  onChange: (e, { newValue }) => setProvider(newValue) // Removed fetchRoutes from onChange
                }}
                theme={{
                  container: 'react-autosuggest__container',
                  input: 'react-autosuggest__input',
                  suggestionsContainer: 'react-autosuggest__suggestions-container',
                  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                  suggestionsList: 'react-autosuggest__suggestions-list',
                  suggestion: 'react-autosuggest__suggestion',
                  suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                }}
              />
            </Form.Group>
          </Col>

          {/* With Schedules Dropdown */}
          <Col md={6}>
            <Form.Group controlId="withSchedules" className="mb-3">
              <Form.Label>With Schedules</Form.Label>
              <Form.Control
                as="select"
                value={withSchedules || ''}
                onChange={(e) => setWithSchedules(e.target.value === '' ? null : e.target.value)}
              >
                <option value="">All</option>
                <option value="1">Routes With Schedules</option>
                <option value="2">Routes Without Schedules</option>
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>

        <Button variant="primary" onClick={handleSearch}>
          <FaSearch /> Search
        </Button>
        <Button variant="outline-secondary" onClick={handleClearFilters} className="ml-2">
          <FaEraser /> Clear Filters
        </Button>
        <div className="info-bar d-flex justify-content-between mt-2 text-small w-100">
          {isLoading ? (
            <div className="d-flex align-items-center">
              Searching... <FaSpinner className="fa-spinner ml-2" />
            </div>
          ) : (
            <div></div>
          )}
          <div>
            Total Records: {totalRecords}
          </div>
        </div>
      </div>
      <div className="actions-dropdown" style={{ position: 'absolute', top: '-15px', right: '10px' }}>
        <Dropdown>
          <Dropdown.Toggle variant="secondary" id="dropdown-options">
            Options
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item onClick={() => navigate(providerType === 'DCC' ? `/routing-guide/create/dcc?providerType=${providerType}` : `/routing-guide/create?providerType=${providerType}`)}>
              <FaPlus /> Create New Route
            </Dropdown.Item>
            <Dropdown.Item onClick={handleDownloadExcel}>
              <FaFileExcel /> Download as Excel
            </Dropdown.Item>
            <Dropdown.Item onClick={handleDownloadSearchResultsToExcel}>
              <FaFileExcel /> Download Search Results as Excel
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <RouteTable
        routes={routes}
        sortConfig={sortConfig}
        requestSort={requestSort}
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
        setSelectedRoute={setSelectedRoute}
        handleEditRoute={(route) => setSelectedRoute(route)}
        providers={providers}
        fetchRoutes={fetchRoutes}
        setSuccessMessage={setSuccessMessage}
        provider={provider}
      />
    </div>
  );
};

export default ListRoutingGuides;
